/**
 * Storefront v6 — the delivery check, one payload rule.
 *
 * Bare postal input (a full CA postal, an FSA prefix like "L4C", or a ZIP /
 * ZIP prefix) goes as postal_code so the server's prefix matcher answers
 * directly; a picked autocomplete suggestion carries its coordinates +
 * postal; anything else is an address. Same rule as the v5.66 checker
 * section (pinned equal by contract) so every checker surface asks the
 * server the same question.
 */
export const buildDeliveryCheckBody = (q, place, storeSlug) => {
  const looksLikePostalOnly =
    /^[A-Za-z]\d[A-Za-z]\s?(?:\d[A-Za-z]\d)?$/.test(q) || /^\d{3,5}(?:-\d{4})?$/.test(q);
  const picked = !looksLikePostalOnly && place ? place : null;
  return {
    address: looksLikePostalOnly ? undefined : q,
    postal_code: looksLikePostalOnly ? q : (picked?.postal_code || undefined),
    lat: picked?.latitude ?? undefined,
    lng: picked?.longitude ?? undefined,
    store_slug: storeSlug || undefined,
  };
};

const DAY = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };

/** POST the check; in the Design Studio preview answer with a sample. */
export const runDeliveryCheck = async ({ query, place, storeSlug, subdomain, isPreview }) => {
  const q = String(query || '').trim();
  if (!q) return null;
  if (isPreview) {
    await new Promise((r) => setTimeout(r, 350));
    return { enabled: true, deliverable: true, method: 'demo', matched_zones: [{ name: 'Sample Zone', delivery_days: ['mon', 'wed', 'fri'] }] };
  }
  const base = process.env.REACT_APP_BACKEND_URL || '';
  const res = await fetch(`${base}/api/storefront/delivery-check/${subdomain}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(buildDeliveryCheckBody(q, place, storeSlug)),
  });
  if (!res.ok) throw new Error(`delivery check failed (${res.status})`);
  return res.json();
};

/** One honest line from the server's answer. */
export const describeDeliveryResult = (result, copy = {}) => {
  if (!result) return null;
  if (result.error) return { tone: 'warn', text: 'Something went wrong checking that address. Please try again.' };
  if (result.enabled === false) return { tone: 'ok', text: copy.success || 'We deliver to you.' };
  if (result.method === 'unresolved') return { tone: 'warn', text: result.message || "We couldn't locate that address. Try adding your city and postal code." };
  if (result.deliverable) {
    const z = (result.matched_zones || [])[0];
    const days = z && Array.isArray(z.delivery_days) && z.delivery_days.length ? z.delivery_days.map((d) => DAY[d] || d).join(' / ') : '';
    return { tone: 'ok', text: copy.success || 'We deliver to you.', detail: [z?.name, days].filter(Boolean).join(' · ') };
  }
  return { tone: 'no', text: copy.failure || "We don't deliver there yet." };
};
